import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  FaMapMarkerAlt, FaPhone, FaEnvelope, FaFacebookF, 
  FaInstagram, FaTwitter, FaLinkedinIn, FaYoutube,
  FaArrowRight, FaClock, FaGraduationCap, FaBookOpen,
  FaUserGraduate, FaHeartbeat, FaQuoteLeft
} from 'react-icons/fa';

const FooterSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const quickLinks = [
    { name: "Home", link: "/" },
    { name: "About Us", link: "/mision-vision" },
    { name: "Admission", link: "/admisionprocess" },
    { name: "Courses", link: "/course-details" },
    { name: "Facilities", link: "/classroom" },
    { name: "Gallery", link: "/gallery" },
    { name: "Contact Us", link: "/contact" }
  ];

  const courses = [
    { name: "Bachelor of Commerce (B.Com)", link: "/course-details" },
    { name: "Bachelor of Science (B.Sc)", link: "/course-details" },
    { name: "Bachelor of Arts (B.A)", link: "/course-details" }
  ];

  const contactInfo = [
    { icon: <FaMapMarkerAlt />, text: "Akhari Bai-Pass, Varanasi, Uttar Pradesh 221011" },
    { icon: <FaPhone />, text: "+91 7080 100 333" },
    { icon: <FaEnvelope />, text: "bnsmahilamahavidyalaya@gmail.com" }
  ];

  const socialLinks = [
    { icon: <FaFacebookF />, name: "Facebook", color: "bg-blue-600", link: "https://www.facebook.com/100092840165594/about/?_rdr" },
    { icon: <FaInstagram />, name: "Instagram", color: "bg-pink-600", link: "https://www.instagram.com/bnswomencollege/" },
    { icon: <FaTwitter />, name: "Twitter", color: "bg-sky-500", link: "https://twitter.com" },
    { icon: <FaLinkedinIn />, name: "LinkedIn", color: "bg-blue-700", link: "https://linkedin.com" },
    { icon: <FaYoutube />, name: "YouTube", color: "bg-red-600", link: "https://youtube.com" }
  ];

  return (
    <footer ref={sectionRef} className="w-full bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 pt-12 pb-6 relative overflow-hidden">
      
      {/* Decorative Background Elements */}
      <div className="absolute top-0 left-0 w-64 h-64 bg-yellow-500/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-400/5 rounded-full blur-3xl"></div>
      
      <div className="w-full px-6 lg:px-12 relative z-10">
        <div className="max-w-7xl mx-auto">
          
          {/* Main Footer Content */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-10">
            
            {/* Column 1 - Logo + College Info (Left Side) */}
            <div className={`space-y-4 transition-all duration-500 ${
              isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`} style={{ transitionDelay: '100ms' }}>
              
              {/* Logo */}
              <div className="flex justify-start">
                <img 
                  src="/logo1.png" 
                  alt="BNS Mahila Maha Vidyalaya Logo" 
                  className="h-12 w-auto object-contain drop-shadow-lg"
                  onError={(e) => e.target.src = 'https://via.placeholder.com/48?text=BNS'}
                />
              </div>
              
              {/* College Name with Decoration */}
              <div>
                <div className="w-16 h-0.5 bg-gradient-to-r from-yellow-400 to-transparent mt-2"></div>
              </div>
              
              {/* Tagline / Motto */}
              <div className="bg-white/5 backdrop-blur-sm rounded-lg p-3 border-l-4 border-yellow-400">
                <FaQuoteLeft className="text-yellow-400 text-xs mb-1" />
                <p className="text-blue-100 text-xs italic leading-relaxed">
                  "Empowering Women Through Quality Education"
                </p>
              </div>
              
              {/* Description */}
              <p className="text-blue-200 text-sm leading-relaxed">
                Affiliated with <span className="text-yellow-300 font-semibold">Mahatma Gandhi Kashi Vidyapith (MGKVP)</span>. 
                Committed to excellence in higher education since 2000+.
              </p>
              {/* Social Links */}
              <div className="flex gap-2 pt-2">
                {socialLinks.map((social, idx) => (
                  <a
                    key={idx}
                    href={social.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-8 h-8 ${social.color} rounded-full flex items-center justify-center text-white text-xs transition-all duration-300 hover:scale-110 hover:opacity-80 hover:shadow-lg`}
                  >
                    {social.icon}
                  </a>
                ))}
              </div>
            </div>

            {/* Column 2 - Quick Links */}
            <div className={`space-y-4 transition-all duration-500 ${
              isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`} style={{ transitionDelay: '200ms' }}>
              <h3 className="text-lg font-bold text-yellow-400 flex items-center gap-2">
                <div className="w-1 h-6 bg-yellow-400 rounded-full"></div>
                Quick Links
              </h3>
              <ul className="space-y-2">
                {quickLinks.map((link, idx) => (
                  <li key={idx}>
                    <Link to={link.link} className="text-blue-200 text-sm hover:text-yellow-400 transition-colors duration-300 flex items-center gap-2 group">
                      <FaArrowRight className="text-yellow-400 text-xs opacity-0 group-hover:opacity-100 transition-all duration-300 -translate-x-2 group-hover:translate-x-0" />
                      <span className="group-hover:translate-x-1 transition-transform duration-300">{link.name}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Column 3 - Courses */}
            <div className={`space-y-4 transition-all duration-500 ${
              isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`} style={{ transitionDelay: '300ms' }}>
              <h3 className="text-lg font-bold text-yellow-400 flex items-center gap-2">
                <div className="w-1 h-6 bg-yellow-400 rounded-full"></div>
                Our Courses
              </h3>
              <ul className="space-y-2">
                {courses.map((course, idx) => (
                  <li key={idx}>
                    <Link to={course.link} className="text-blue-200 text-sm hover:text-yellow-400 transition-colors duration-300 flex items-center gap-2 group">
                      <FaBookOpen className="text-yellow-400 text-xs opacity-0 group-hover:opacity-100 transition-all duration-300" />
                      <span className="group-hover:translate-x-1 transition-transform duration-300">{course.name}</span>
                    </Link>
                  </li>
                ))}
              </ul>
              
              {/* Admission Badge */}
              <div className="pt-3">
                <div className="bg-gradient-to-r from-yellow-500/20 to-yellow-600/10 rounded-lg p-3 border border-yellow-500/30">
                  <div className="flex items-center gap-2 mb-1">
                    <FaUserGraduate className="text-yellow-400 text-sm animate-pulse" />
                    <span className="text-xs font-bold text-yellow-300">Admissions Open 2024-25</span>
                  </div>
                  <p className="text-xs text-blue-200">Limited seats available. Apply now!</p>
                </div>
              </div>
            </div>

            {/* Column 4 - Contact Info */}
            <div className={`space-y-4 transition-all duration-500 ${
              isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`} style={{ transitionDelay: '400ms' }}>
              <h3 className="text-lg font-bold text-yellow-400 flex items-center gap-2">
                <div className="w-1 h-6 bg-yellow-400 rounded-full"></div>
                Get In Touch
              </h3>
              <div className="space-y-3">
                {contactInfo.map((info, idx) => (
                  <div key={idx} className="flex items-start gap-3 group">
                    <div className="text-yellow-400 text-sm mt-0.5 group-hover:scale-110 transition-transform duration-300">{info.icon}</div>
                    <p className="text-blue-200 text-sm group-hover:text-white transition-colors duration-300">{info.text}</p>
                  </div>
                ))}
              </div>
              
              {/* Timing */}
              <div className="pt-2">
                <div className="flex items-center gap-2 text-blue-200 text-sm bg-white/5 rounded-lg p-2">
                  <FaClock className="text-yellow-400" />
                  <span>Mon - Sat: <span className="text-white font-medium">9:00 AM - 5:00 PM</span></span>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className={`border-t border-blue-700/50 pt-6 mt-4 transition-all duration-500 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`} style={{ transitionDelay: '500ms' }}>
            <div className="flex flex-col md:flex-row justify-between items-center gap-3">
              <p className="text-blue-300 text-xs text-center md:text-left">
                © 2024 BNS Mahila Maha Vidyalaya. All rights reserved. | Designed with ❤️ for education
              </p>
              <div className="flex gap-6">
                <Link to="/privacy" className="text-blue-300 text-xs hover:text-yellow-400 transition-colors duration-300">Privacy Policy</Link>
                <Link to="/terms" className="text-blue-300 text-xs hover:text-yellow-400 transition-colors duration-300">Terms & Conditions</Link>
                <Link to="/sitemap" className="text-blue-300 text-xs hover:text-yellow-400 transition-colors duration-300">Sitemap</Link>
              </div>
            </div>
          </div>

          {/* Bottom Decorative Dots */}
          <div className="mt-6 flex justify-center gap-2">
            <div className="w-2 h-2 bg-yellow-400 rounded-full animate-bounce"></div>
            <div className="w-2 h-2 bg-yellow-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
            <div className="w-2 h-2 bg-yellow-600 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-6px); }
        }
        .animate-bounce {
          animation: bounce 1s ease-in-out infinite;
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        .animate-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
      `}</style>
    </footer>
  );
};

export default FooterSection;